import { createFileRoute, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { useAuthStore } from "src/auth";
import { InstallMobileAppDialog } from "src/components/InstallMobileAppDialog";
import { env } from "src/env";
import {
  calendarSearchSchema,
  getCalendarInitialView,
} from "src/lib/calendarViewRoute";
import { loaderHandler } from "src/lib/loaderHandler";
import { AthleteCalendarPage } from "src/pages/AthleteCalendarPage";
import { CoachCalendarPage } from "src/pages/CoachCalendarPage";
import { LandingPage } from "src/pages/LandingPage";
import { fetchCalendarInitialData } from "src/queries/calendar";
import { organizationsQueryOptions } from "src/queries/organizations";

const routeSearchSchema = calendarSearchSchema.extend({
  email: z.string().optional(),
  installMobileApp: z.boolean().optional(),
});

export const Route = createFileRoute("/")({
  component: RouteComponent,
  validateSearch: routeSearchSchema,
  ssr: true,
  loader: async ({ context, location }) => {
    return loaderHandler(async ({ session, i18n }) => {
      if (
        session?.coach &&
        session.user.tier === "FREE" &&
        session.entitlements.canAccessOrganizations
      ) {
        const organizations = await context.queryClient.query({
          ...organizationsQueryOptions(),
          staleTime: "static",
        });
        const organization = organizations[0];

        if (organization) {
          throw redirect({
            to: "/orgs/$slug",
            params: { slug: organization.slug },
          });
        }
      }

      if (session) {
        const includeWorkflow = Boolean(session.athlete);
        const calendar = getCalendarInitialView(location.href, "fixed");
        const initialData = await fetchCalendarInitialData(
          { type: "calendar", athleteId: undefined, includeWorkflow },
          calendar,
        );

        return {
          data: {
            type: session.coach ? ("coach" as const) : ("athlete" as const),
            includeWorkflow,
            calendar,
            initialData,
          },
        };
      }

      return {
        data: {
          type: "landing" as const,
          locale: i18n.language,
          og: {
            title: i18n.t("og.title"),
            description: i18n.t("og.description"),
          },
        },
      };
    });
  },
  head({ loaderData }) {
    if (
      loaderData?.data?.type === "coach" ||
      loaderData?.data?.type === "athlete"
    ) {
      return { meta: [] };
    }

    const fallbackOg = {
      title: "sztama - Training programs ready in minutes.",
      description:
        "Create programs, track workouts, and keep coaching work in one place.",
    };
    const locale =
      loaderData?.data?.type === "landing" ? loaderData.data.locale : "pl";
    const og =
      loaderData?.data?.type === "landing" ? loaderData.data.og : fallbackOg;
    const baseUrl = env.VITE_APP_WEB_ORIGIN || "https://sztama.app";
    const isPolish = locale.startsWith("pl");
    const ogLocale = isPolish ? "pl_PL" : "en_US";
    const ogImage = `${baseUrl}/${isPolish ? "og-image-pl.png" : "og-image.png"}`;

    return {
      meta: [
        { title: og.title },
        { name: "description", content: og.description },
        // OpenGraph
        { property: "og:title", content: og.title },
        { property: "og:description", content: og.description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: baseUrl },
        { property: "og:image", content: ogImage },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { property: "og:image:alt", content: og.title },
        { property: "og:site_name", content: "sztama" },
        { property: "og:locale", content: ogLocale },
        // Twitter
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: og.title },
        { name: "twitter:description", content: og.description },
        { name: "twitter:image", content: ogImage },
        { name: "twitter:image:alt", content: og.title },
      ],
    };
  },
  staticData: {
    getScrollRestoration: () => {
      return false;
    },
    getBreadcrumb: () => {
      return {
        href: "/",
        coach: {
          labelTranslationKey: "schedule",
        },
        athlete: {
          labelTranslationKey: "schedule",
        },
      };
    },
  },
});

function RouteComponent() {
  const authStore = useAuthStore();
  const loaderData = Route.useLoaderData();
  const navigate = Route.useNavigate();
  const search = Route.useSearch();

  function closeInstallMobileAppDialog() {
    void navigate({
      replace: true,
      search: (previous) => ({
        ...previous,
        installMobileApp: undefined,
      }),
    });
  }

  if (!authStore.isAuthenticated) {
    return <LandingPage />;
  }

  if (authStore.isAthlete) {
    if (loaderData?.data?.type !== "athlete") {
      return null;
    }

    return (
      <>
        <AthleteCalendarPage
          calendar={loaderData.data.calendar}
          initialData={loaderData.data.initialData}
        />
        <InstallMobileAppDialog
          open={search.installMobileApp === true}
          onContinue={closeInstallMobileAppDialog}
        />
      </>
    );
  }

  if (loaderData?.data?.type !== "coach") {
    return null;
  }

  return (
    <CoachCalendarPage
      includeWorkflow={loaderData.data.includeWorkflow}
      calendar={loaderData.data.calendar}
      initialData={loaderData.data.initialData}
    />
  );
}
